import React, { useState, useEffect, useRef } from "react";
import { useQuery } from '@tanstack/react-query';
import Header from './Header';
import Footer from './Footer';
import Aside from './Aside';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate, useSearchParams } from "react-router-dom";
import hello from '../assets/images/hello-visuals-featured-a1.png';
import Confetti from './confetti';

const fetchImages = async () => {
    const authData = localStorage.getItem('auth');
    const token = authData ? JSON.parse(authData).token : null;
    const id = authData ? JSON.parse(authData).id : null;
    const baseurl = `${process.env.REACT_APP_BASE_URL}/plans`;

    if (!token || !id) {
        throw new Error("User authentication data is missing.");
    }

    const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/get-images`,
        {
            params: { user_id: id, app_url: baseurl },
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
    );

    if (response.data.status !== 200) {
        throw new Error('Error fetching images');
    }

    return {
        images: response.data.images,
        info: response.data.info.image,
    };
};

const Images = () => {
    const [charCount, setCharCount] = useState(1000);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [activeTab, setActiveTab] = useState("texts-tab");
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [textareaValue, setTextareaValue] = useState("");
    const [file, setFile] = useState(null);
    const [captionsText1, setCaptionsText1] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [captionimg, setCaptionimg] = useState('');
    const confettiRef = useRef(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isModalImgOpen, setIsModalImgOpen] = useState(false);
    const [type, setType] = useState('waiting');
    const [loaderType, setLoaderType] = useState('loaderType1');
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadStatus, setDownloadStatus] = useState('Download');
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Use React Query to fetch images
    const { data, isFetching, isError } = useQuery({
        queryKey: ['images'],
        queryFn: fetchImages,
        staleTime: Infinity, // Data never becomes stale automatically
        cacheTime: Infinity, // Cache data indefinitely
        refetchOnWindowFocus: false, // Disable refetch on window focus
        refetchOnMount: true, // Refetch when component mounts
        keepPreviousData: true, // Keep previous data while fetching new data
    });

    // Extract images and info from fetched data, fallback to empty values if undefined
    const images = data?.images || [];
    const captionsText = data?.info || {};

    useEffect(() => {
        if (captionsText1.length > 0) {
            setSelectedImage(captionsText1[0]);
        }
    }, [captionsText1]);

    const startAndStopConfetti = () => {
        if (confettiRef.current) {
            confettiRef.current.startConfetti();
            setTimeout(() => {
                if (confettiRef.current) {
                    confettiRef.current.stopConfetti();
                }
            }, 4600);
        }
    };

    const handleGenerateText = async () => {
        setCaptionsText1("");
        setSelectedImage("");
        setType('waiting');
        setIsModalImgOpen(true);
        setLoading(true);
        setError(null);
        const authData = localStorage.getItem("auth");
        const token = authData ? JSON.parse(authData).token : null;
        const id = authData ? JSON.parse(authData).id : null;
        if (!token || !id) {
            console.error("User authentication data is missing.");
            return;
        }
        try {
            const formData = new FormData();
            formData.append("_token", token);
            formData.append("user_id", id);
            if (activeTab === "texts-tab") {
                formData.append("type", "mid");
                formData.append("prompt", textareaValue.trim());
                const numCharactersUsed = 1000 - charCount;
                formData.append("category_prompt_id", numCharactersUsed);
                if (captionimg) {
                    formData.append("file_mid", captionimg);
                }
            } else if (activeTab === "profile-tab") {
                formData.append("type", "mid");
                if (captionimg) {
                    formData.append("file_mid", captionimg);
                    formData.append("image", captionimg);
                }
                formData.append("prompt", textareaValue.trim());
            }

            formData.append("tab", activeTab === "texts-tab" ? "text" : "img");

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/generate`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            if (response.data.status === 200) {
                setCaptionsText1([
                    response.data.response.img1,
                    response.data.response.img2,
                    response.data.response.img3,
                    response.data.response.img4,
                ]);
                captionsText.alertImgMsg = response.data.info.image.alertImgMsg || captionsText.alertImgMsg;
                captionsText.imgCounts = response.data.info.image.imgCounts || captionsText.imgCounts;
                const targetElement = document.getElementById('#caponresult');
                if (targetElement) {
                    targetElement.scrollIntoView({ behavior: 'smooth' });
                }
                setIsModalImgOpen(false);
                startAndStopConfetti();
            } else if (response.data.status === 422) {
                setError(response.data.message);
            } else {
                console.error("Error generating images:", response.data);
            }
            setLoading(false);
            setIsModalImgOpen(false);
        } catch (error) {
            setLoading(false);
            setIsModalImgOpen(false);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const allowedTypes = ["image/png", "image/jpg", "image/jpeg", "image/webp", "image/svg+xml"];
        if (!allowedTypes.includes(file.type)) {
            Swal.fire({
                text: 'Invalid file type! Please upload a .png, .jpg, .jpeg, .webp, or .svg file.',
                icon: 'error',
                title: 'Error!',
                confirmButtonText: 'Ok',
            });
            event.target.value = "";
            return;
        }
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setGeneratedImage(reader.result);
            };
            setCaptionimg(file);
            reader.readAsDataURL(file);
        }
    };

    const imgClose = (type) => {
        if (type === "mid") {
            setGeneratedImage(null);
            document.getElementById('file-upload-chat').value = null;
        }
    };

    useEffect(() => {
        const categorySlug = searchParams.get("category");
        if (categorySlug) {
            const category = images.find((image) => image.slug === categorySlug);
            setSelectedCategory(category || null);
        } else {
            setSelectedCategory(null);
        }
    }, [searchParams, images]);

    const handleCategoryClick = (image) => {
        setSelectedCategory(image);
        navigate(`/images?category=${image.slug}`);
    };

    const handleBackClick = () => {
        setSelectedCategory(null);
        navigate("/images");
    };

    const handlePromptClick = (promptText) => {
        setTextareaValue(promptText);
        setCharCount(1000 - promptText.length);
        setSelectedPrompt(promptText);
        const targetElement = document.getElementById('#capon');
        if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleTextChange = (e) => {
        const value = e.target.value;
        setTextareaValue(value);
        setCharCount(1000 - value.length);
    };

    const downloadImage = async (src) => {
        if (!src) {
            setDownloadStatus('No image selected');
            return;
        }
        const filename = src.split('/').pop();
        setIsDownloading(true);
        setDownloadStatus('Downloading...');
        const urlSegments = src.split('/');
        const file_id = urlSegments[urlSegments.length - 2];
        const authData = localStorage.getItem('auth');
        const token = authData ? JSON.parse(authData).token : null;
        const user_id = authData ? JSON.parse(authData).id : null;
        const apiUrl = `${process.env.REACT_APP_API_URL}/api/download-image/${filename}?user_id=${user_id}&file_id=${file_id}&type=mid-images`;
        try {
            const response = await axios.get(apiUrl, {
                headers: { "Authorization": `Bearer ${token}` },
                responseType: 'blob',
            });
            const blob = response.data;
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            link.click();
            URL.revokeObjectURL(link.href);
            setDownloadStatus('Downloaded');
        } catch (error) {
            console.error('Error downloading image:', error);
            setDownloadStatus('Download Failed');
        } finally {
            setTimeout(() => {
                setIsDownloading(false);
                setDownloadStatus('Download');
            }, 2000);
        }
    };

    const handleImageClick = (imageSrc) => {
        setSelectedImage(imageSrc);
    };

    function genLoader() {
        return (
            <div>
                {loaderType === 'loaderType1' ? (
                    <svg
                        viewBox="0 0 64 64"
                        fill="blue"
                        xmlns="http://www.w3.org/2000/svg"
                        className="gen-loader"
                        style={{ width: '200px', height: '170px', margin: 'auto', display: 'block', }} >
                        <path pathLength="360" d="M 56.3752 2 H 7.6248 C 7.2797 2 6.9999 2.268 6.9999 2.5985 V 61.4015 C 6.9999 61.7321 7.2797 62 7.6248 62 H 56.3752 C 56.7203 62 57.0001 61.7321 57.0001 61.4015 V 2.5985 C 57.0001 2.268 56.7203 2 56.3752 2 Z"></path>
                        <path pathLength="360" d="M 56.3752 2 H 7.6248 C 7.2797 2 6.9999 2.268 6.9999 2.5985 V 61.4015 C 6.9999 61.7321 7.2797 62 7.6248 62 H 56.3752 C 56.7203 62 57.0001 61.7321 57.0001 61.4015 V 2.5985 C 57.0001 2.268 56.7203 2 56.3752 2 Z"></path>
                        <path pathLength="360" d="M 55.7503 60.803 H 8.2497 V 3.1971 H 55.7503 V 60.803 Z"></path>
                        <path pathLength="360" d="M 29.7638 47.6092 C 29.4971 47.3997 29.1031 47.4368 28.8844 47.6925 C 28.6656 47.9481 28.7046 48.3253 28.9715 48.5348 L 32.8768 51.6023 C 32.9931 51.6936 33.1333 51.738 33.2727 51.738 C 33.4533 51.738 33.6328 51.6634 33.7562 51.519 C 33.975 51.2634 33.936 50.8862 33.6692 50.6767 L 29.7638 47.6092 Z"></path>
                        <path pathLength="360" d="M 42.3557 34.9046 C 38.4615 34.7664 36.9617 37.6749 36.7179 39.2213 L 35.8587 44.2341 C 35.8029 44.5604 36.0335 44.8681 36.374 44.9218 C 36.4084 44.9272 36.4424 44.9299 36.476 44.9299 C 36.7766 44.9299 37.0415 44.7214 37.0918 44.4281 L 37.9523 39.4076 C 37.9744 39.2673 38.544 35.9737 42.311 36.1007 C 42.6526 36.1124 42.9454 35.8544 42.9577 35.524 C 42.9702 35.1937 42.7006 34.9164 42.3557 34.9046 Z"></path>
                        <path pathLength="360" d="M 13.1528 55.5663 C 13.1528 55.8968 13.4326 56.1648 13.7777 56.1648 H 50.2223 C 50.5674 56.1648 50.8472 55.8968 50.8472 55.5663 V 8.4339 C 50.8472 8.1034 50.5674 7.8354 50.2223 7.8354 H 13.7777 C 13.4326 7.8354 13.1528 8.1034 13.1528 8.4339 V 55.5663 Z"></path>
                        <path pathLength="360" d="M 25.3121 26.5567 C 24.9717 27.4941 25.0042 28.8167 25.0634 29.5927 C 23.6244 29.8484 20.3838 31.0913 18.9478 37.0352 C 18.5089 37.5603 17.8746 38.1205 17.2053 38.7114 C 16.2598 39.546 15.2351 40.4515 14.4027 41.5332 V 20.5393 H 23.7222 C 23.7178 22.6817 24.1666 25.4398 25.3121 26.5567 Z"></path>
                        <path pathLength="360" d="M 49.5975 43.4819 C 48.3838 39.1715 46.3138 33.6788 43.4709 29.7736 C 42.6161 28.5995 40.7095 27.0268 39.6852 26.1818 L 39.6352 26.1405 C 39.4176 24.783 39.1158 22.5803 38.8461 20.5394 H 49.5976 V 43.4819 Z"></path>
                        <path pathLength="360" d="M 29.8161 45.151 C 29.0569 44.7516 28.3216 44.4344 27.6455 44.185 C 27.6488 44.0431 27.6397 43.8917 27.6478 43.7715 C 27.9248 39.7036 30.4491 36.2472 35.1502 33.4979 C 38.7221 31.4091 42.2682 30.5427 42.3036 30.5341 C 42.3563 30.5213 42.416 30.5119 42.4781 30.5037 C 42.6695 30.7681 42.8577 31.0407 43.0425 31.3217 C 42.1523 31.4917 39.6591 32.0721 37.0495 33.6188 C 34.2273 35.2912 30.7775 38.4334 29.9445 44.0105 C 29.9025 44.2924 29.8211 45.0524 29.8161 45.151 Z"></path>
                        <path pathLength="360" d="M 32.2021 33.6346 C 29.1519 33.8959 26.6218 32.5634 25.6481 31.4461 C 25.9518 30.3095 28.4436 28.4847 30.2282 27.4911 C 30.436 27.3755 30.5563 27.1556 30.5372 26.9261 L 30.4311 25.6487 C 30.5264 25.6565 30.622 25.6621 30.7181 25.6642 L 30.8857 25.6672 C 32.0645 25.6912 33.2094 25.302 34.1059 24.5658 L 34.112 24.5607 L 34.4024 32.5344 C 33.8302 32.8724 33.2863 33.2227 32.7728 33.5852 C 32.5227 33.6032 32.3068 33.6258 32.2021 33.6346 Z"></path>
                        <path pathLength="360" d="M 27.8056 17.9207 C 27.8041 17.9207 27.8025 17.9207 27.8012 17.9207 L 27.0155 17.9259 L 26.8123 15.4718 C 26.8174 15.4609 26.8238 15.4501 26.8282 15.4389 C 27.2218 15.0856 28.158 14.3463 29.1923 14.252 C 31.0985 14.0778 33.442 14.3386 33.8213 16.5565 L 34.0564 23.0299 L 33.2927 23.6566 C 32.6306 24.2004 31.7888 24.4889 30.9118 24.4703 L 30.7437 24.4673 C 29.7977 24.4473 28.8841 24.0555 28.2376 23.3933 C 27.9671 23.1152 27.748 22.7967 27.5871 22.4474 C 27.426 22.0961 27.3292 21.7272 27.2989 21.3494 L 27.1145 19.1223 L 27.8097 19.1178 C 28.1548 19.1154 28.4327 18.8457 28.4303 18.5152 C 28.4278 18.186 28.1487 17.9207 27.8056 17.9207 Z"></path>
                        <path pathLength="360" d="M 38.4358 26.5433 C 38.4589 26.6829 38.5326 26.8101 38.6443 26.9026 L 38.8697 27.0889 C 39.5266 27.6307 40.6931 28.5938 41.5811 29.4829 C 40.6409 29.7428 38.2545 30.4762 35.6283 31.8516 L 35.3161 23.281 C 35.316 23.2777 35.3158 23.2743 35.3157 23.271 L 35.0692 16.4785 C 35.0682 16.455 35.0659 16.4316 35.0621 16.4082 C 34.6703 13.9692 32.4875 12.7498 29.0741 13.0603 C 28.5659 13.1067 28.0885 13.255 27.6614 13.4468 C 28.321 12.6324 29.4568 11.8605 31.3984 11.8605 C 32.892 11.8605 34.2086 12.4323 35.3118 13.5599 C 36.3478 14.6187 36.9981 15.9821 37.1923 17.5023 C 37.5097 19.987 38.0932 24.4655 38.4358 26.5433 Z"></path>
                        <path pathLength="360" d="M 25.6994 17.1716 L 26.053 21.4425 C 26.094 21.9536 26.225 22.4539 26.4434 22.93 C 26.6613 23.403 26.9574 23.8335 27.3242 24.2106 C 27.833 24.7317 28.4641 25.128 29.1549 25.3746 L 29.2609 26.6526 C 28.8063 26.9219 27.959 27.4459 27.0978 28.0926 C 26.7982 28.3177 26.5261 28.5365 26.2766 28.7503 C 26.2677 27.9385 26.3477 27.0941 26.6128 26.699 C 26.7087 26.5561 26.7368 26.3807 26.6898 26.2168 C 26.6428 26.0528 26.5253 25.9159 26.3667 25.8398 C 25.2812 25.3198 24.639 20.7943 25.134 18.7283 C 25.2757 18.1366 25.4822 17.6126 25.6994 17.1716 Z"></path>
                        <path pathLength="360" d="M 14.4025 54.9677 V 43.9616 C 15.1297 42.1745 16.6798 40.8031 18.052 39.5917 C 18.5756 39.1296 19.0771 38.6852 19.5054 38.243 C 20.1455 38.2763 21.8243 38.4721 22.2856 39.611 C 22.526 40.696 22.9861 41.6387 23.6573 42.3985 C 23.7809 42.5383 23.9573 42.6104 24.1347 42.6104 C 24.2773 42.6104 24.4206 42.5639 24.5381 42.4688 C 24.8014 42.2553 24.8343 41.8776 24.6115 41.6252 C 22.2978 39.0062 23.8504 34.5445 23.8663 34.4997 C 23.9782 34.1872 23.8046 33.8471 23.4785 33.7397 C 23.1507 33.6321 22.7964 33.7986 22.6843 34.1111 C 22.6657 34.1631 22.2262 35.4024 22.1149 37.0253 C 22.0992 37.2529 22.0927 37.476 22.0916 37.6958 C 21.4663 37.3478 20.7678 37.1827 20.215 37.1057 C 21.266 32.9598 23.2109 31.5061 24.4867 30.9973 C 24.4164 31.2001 24.3769 31.3974 24.3692 31.5894 C 24.3639 31.7208 24.404 31.8501 24.4831 31.9575 C 25.0708 32.7551 26.1363 33.5207 27.4065 34.0584 C 28.2686 34.4232 29.5576 34.8194 31.1457 34.861 C 28.2499 37.3877 26.6257 40.39 26.4009 43.6936 C 26.3992 43.7195 26.3962 43.7461 26.3928 43.7729 C 25.1023 43.399 24.2167 43.2969 24.1252 43.2873 C 23.9888 43.2728 23.8487 43.3023 23.7304 43.3716 C 23.0495 43.7702 22.591 44.3922 22.4046 45.1703 C 22.2331 45.8868 22.3106 46.6885 22.6019 47.3807 C 22.0046 47.6438 21.3269 47.7784 20.7914 47.848 C 19.4939 45.6912 20.8219 44.6351 20.989 44.5146 C 21.2655 44.3207 21.3274 43.9492 21.1268 43.6822 C 20.9253 43.4139 20.5346 43.3533 20.2546 43.5462 C 19.4539 44.0983 18.406 45.6195 19.3656 47.7888 C 18.685 47.5329 17.6255 46.8145 17.8055 44.832 C 17.8836 43.9718 18.1884 43.3352 18.7117 42.9403 C 19.5815 42.2834 20.8198 42.451 20.8366 42.4537 C 21.1748 42.503 21.4952 42.2819 21.5494 41.9563 C 21.6037 41.6297 21.3713 41.3231 21.0306 41.2712 C 20.9582 41.2599 19.2558 41.0142 17.9494 41.9917 C 17.1375 42.5992 16.6703 43.5199 16.5605 44.7282 C 16.1991 48.7092 19.7376 49.1126 19.7732 49.116 C 19.7951 49.1182 22.2326 49.1079 23.7782 48.1211 C 23.8053 48.1039 24.4158 47.7528 24.4158 47.7528 C 24.5214 47.8841 24.6624 48.0532 24.8294 48.2438 L 22.3598 49.4874 C 22.1544 49.5908 22.0257 49.7949 22.0257 50.0171 V 51.8127 C 22.0257 52.1432 22.3054 52.4112 22.6505 52.4112 S 23.2754 52.1432 23.2754 51.8127 V 50.3786 L 25.6987 49.1582 C 26.021 49.4709 26.3894 49.7985 26.7963 50.1188 L 24.6627 50.7144 C 24.4768 50.7663 24.3269 50.8977 24.2559 51.0702 L 23.3968 53.1651 C 23.2704 53.4729 23.4286 53.8202 23.7498 53.9409 C 23.8248 53.9694 23.9023 53.9825 23.9782 53.9825 C 24.2277 53.9825 24.4632 53.8384 24.5599 53.6028 L 25.307 51.7814 L 28.0879 51.0053 C 28.5412 51.2713 29.0239 51.51 29.5341 51.6979 C 29.6079 51.7252 29.6836 51.738 29.7582 51.738 C 30.0092 51.738 30.246 51.592 30.3415 51.3542 C 30.4653 51.0457 30.3048 50.6994 29.9825 50.5808 C 27.1642 49.5423 25.2952 46.9394 25.2771 46.9138 C 25.1245 46.6979 24.8439 46.6013 24.5831 46.6746 L 23.7537 46.9082 C 23.5672 46.4465 23.5125 45.8992 23.623 45.4377 C 23.7168 45.046 23.9138 44.7341 24.21 44.508 C 25.267 44.6734 29.863 45.5842 33.2732 49.2905 C 33.3967 49.4247 33.569 49.4932 33.7423 49.4932 C 33.889 49.4932 34.0364 49.444 34.1551 49.3437 C 34.414 49.1251 34.439 48.747 34.2108 48.4989 C 33.9947 48.2641 33.7738 48.0421 33.5507 47.8278 L 38.211 47.0175 C 38.3595 47.0014 40.1672 46.8356 41.295 48.2161 C 41.4182 48.3671 41.6019 48.4458 41.7875 48.4458 C 41.9222 48.4458 42.0578 48.4043 42.1721 48.3186 C 42.4439 48.1148 42.4919 47.7386 42.2791 47.4784 C 40.6703 45.5094 38.1379 45.8184 38.0305 45.8327 C 38.0218 45.8339 38.0132 45.8353 38.0043 45.8368 L 32.3855 46.8136 C 31.945 46.4667 31.4998 46.1528 31.0557 45.8697 C 31.0618 45.5534 31.0651 45.1775 31.0836 44.9842 C 31.1138 44.6713 31.1524 44.3635 31.1997 44.0606 C 31.8329 40.0032 34.0061 36.8432 37.6695 34.6587 C 40.6334 32.8915 43.5195 32.4536 43.5682 32.4464 C 43.604 32.4413 43.663 32.4341 43.7302 32.4251 C 47.2229 38.3378 49.3982 46.7588 49.5976 49.5158 V 54.9673 H 14.4025 Z"></path>
                        <path pathLength="360" d="M 49.5975 9.0325 V 19.3422 H 38.689 C 38.5937 18.6105 38.5061 17.9301 38.4329 17.3569 C 38.2063 15.5828 37.4422 13.9868 36.2237 12.7413 C 34.8748 11.3624 33.2514 10.6633 31.3984 10.6633 C 27.3688 10.6633 25.8233 13.5309 25.556 15.0901 C 25.1526 15.5932 24.3175 16.7856 23.916 18.46 C 23.8568 18.7069 23.8106 19.0066 23.7778 19.3421 H 14.4025 V 9.0323 H 49.5975 Z"></path>
                        <path pathLength="360" d="M 30.2223 21.2875 C 30.5674 21.2875 30.8471 21.0195 30.8471 20.6889 V 18.92 L 31.9916 18.9675 C 32.3376 18.9833 32.628 18.7259 32.643 18.3956 C 32.658 18.0654 32.3907 17.786 32.0459 17.7717 L 30.2495 17.6969 C 30.077 17.6889 29.9133 17.7497 29.7902 17.8624 C 29.6671 17.9753 29.5976 18.1315 29.5976 18.2948 V 20.6889 C 29.5974 21.0195 29.8772 21.2875 30.2223 21.2875 Z"></path>
                    </svg>
                ) : (
                    <p>No loader type selected.</p>
                )}
            </div>
        );
    }

    useEffect(() => {
        if (isModalOpen || isModalImgOpen) {
            const timer1 = setTimeout(() => setType("processing"), 2000);
            const timer2 = setTimeout(() => setType("installing"), 10000);
            return () => {
                clearTimeout(timer1);
                clearTimeout(timer2);
            };
        }
    }, [isModalOpen, isModalImgOpen]);

    const [isOpen, setIsOpen] = useState(false);

    const toggleAccordion = () => {
        setIsOpen(!isOpen);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
    };

    const handleBackdropClick = (e) => {
        if (e.target.id === 'modalBackdrop') {
            setIsModalOpen(false);
        }
    };

    const imgClick = (image) => {
        setSelectedImage(image);
        setIsModalOpen(true);
    };

    useEffect(() => {
        if (isModalOpen || isModalImgOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isModalOpen, isModalImgOpen]);

    const handlePrevImage = (event) => {
        const currentIndex = captionsText1.indexOf(selectedImage);
        const prevIndex = (currentIndex - 1 + captionsText1.length) % captionsText1.length;
        setSelectedImage(captionsText1[prevIndex]);
    };

    const handleNextImage = (event) => {
        const currentIndex = captionsText1.indexOf(selectedImage);
        const nextIndex = (currentIndex + 1) % captionsText1.length;
        setSelectedImage(captionsText1[nextIndex]);
    };

    return (
        <div>
            <Header />
            <div style={{ display: 'flex' }}>
                <div style={{ zIndex: '101', background: 'white', boxShadow: '0 .8rem 1rem rgba(0, 0, 0, .15)', position: 'fixed', height: '100vh' }}>
                    <Aside />
                </div>
                <div className='mainstylle'>
                    <Confetti ref={confettiRef} />
                    <div id="kt_content_container" className="paddtop" style={{ paddingBottom: '40px' }}>
                        <section className="generate-main">
                            <div className="container py-4 px-2">
                                <div className="row">
                                    <div className="col-md-12 text-center">
                                        <h1 className="text-center fw-bold pt-3 text-linear" style={{ fontSize: "35px" }}>
                                            Generate AI Images in Seconds
                                        </h1>
                                    </div>
                                </div>
                            </div>
                            <div className="pb-3 captionMargin" style={{ display: 'flex', gap: '20px', width: '100%' }}>
                                {/* First Column */}
                                <div className="d-block d-sm-block d-md-none d-lg-none accordioncap" style={{ backgroundColor: '#fff', boxShadow: '0 0.5rem 1.5rem 0.5rem rgba(0, 0, 0, 0.075)' }}>
                                    <div className={`accordion-headercap ${isOpen ? 'open' : ''}`} onClick={toggleAccordion}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <h5 style={{ fontSize: '1.5rem', textTransform: 'uppercase' }}>Prompt Suggestions</h5>
                                            <i className={`ki-outline fs-3 ${isOpen ? 'ki-up' : 'ki-down'}`}></i>
                                        </div>
                                    </div>
                                    <div className={`accordion-bodycap ${isOpen ? 'open' : ''}`} style={{ maxHeight: isOpen ? '500px' : '0px', transition: 'max-height 0.3s ease' }}>
                                        <div style={{ padding: '20px' }}>
                                            <div style={{ display: selectedCategory ? "none" : "block", height: '430px', overflowY: 'auto', overflowX: 'hidden' }}>
                                                <div className="row caption-prompts prompt-templates-content-c prompt-templates-content">
                                                    {!images.length ? (
                                                        <div className="row skeleton-loader-row">
                                                            {new Array(21).fill(null).map((_, index) => (
                                                                <div key={index} className="col-4 skeleton-loader animated-card3 animated-card"></div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        images.map((image) => (
                                                            <div key={image.id} className="col-4 pl-1 pb-2 pt-0 pr-0">
                                                                <button
                                                                    className="btn btn-white-default text-left w-100 h-100 p-0 category-btn"
                                                                    onClick={() => handleCategoryClick(image)}
                                                                >
                                                                    <i className={`ki-outline ki-${image.icon} fs-2x p-7`}></i>
                                                                    <h5 className="p-0 text-break">{image.name}</h5>
                                                                </button>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                            {selectedCategory && (
                                                <div className="row mt-1">
                                                    <div className="prompt-templates-header mb-3" style={{ borderBottom: '1.5px solid #ddd' }}>
                                                        <button className="btn" onClick={handleBackClick}>
                                                            <i className="ki-outline ki-left-square fs-2x"></i>
                                                        </button>
                                                        <h4 className="mb-3 text-center">{selectedCategory.name}</h4>
                                                    </div>
                                                    <div style={{ overflowY: 'auto', height: '370px' }}>
                                                        {selectedCategory.category_prompts.length > 0 ? (
                                                            selectedCategory.category_prompts.map((prompt) => (
                                                                <div key={prompt.id} className="col-12 mb-3">
                                                                    <div
                                                                        className={`prompt-card p-3 rounded ${selectedPrompt === prompt.prompt ? 'selected' : ''}`}
                                                                        onClick={() => handlePromptClick(prompt.prompt)}
                                                                        style={{
                                                                            borderBottom: '1.5px solid #ddd',
                                                                            cursor: "pointer",
                                                                            color: selectedPrompt === prompt.prompt ? '#0a58ca' : '#2f2f2f',
                                                                        }}
                                                                    >
                                                                        <p className="prompts-text">
                                                                            <a className="prompts-link prompt-chat-links prompt-chat-26">{prompt.prompt}</a>
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <p>No prompts available for this category.</p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="prompt-templates d-none d-sm-none d-md-block d-lg-block" style={{ width: '100%', marginTop: '30px', borderRadius: '10px', background: '#fff', padding: '20px', float: 'left', overflowY: 'auto', height: '670px' }}>
                                    <div className="prompt-templates-header mb-3">
                                        <h2 className="pt-3 pb-3" style={{ textAlign: "center", color: "#000", textTransform: "uppercase", display: selectedCategory ? "none" : "block" }}>Prompt Suggestions</h2>
                                        <hr style={{ display: selectedCategory ? "none" : "block" }} />
                                        <div>
                                            <div style={{ display: selectedCategory ? "none" : "block", height: '530px', overflowY: 'auto', overflowX: 'hidden' }}>
                                                <div className="row caption-prompts prompt-templates-content-c prompt-templates-content">
                                                    {!images.length ? (
                                                        <div className="row skeleton-loader-row">
                                                            {new Array(21).fill(null).map((_, index) => (
                                                                <div key={index} className="col-4 skeleton-loader animated-card3 animated-card"></div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        images.map((image) => (
                                                            <div key={image.id} className="col-4 pl-1 pb-2 pt-0 pr-0">
                                                                <button
                                                                    className="btn btn-white-default text-left w-100 h-100 p-0 category-btn"
                                                                    onClick={() => handleCategoryClick(image)}
                                                                >
                                                                    <i className={`ki-outline ki-${image.icon} fs-2x p-7`}></i>
                                                                    <h5 className="p-0 text-break">{image.name}</h5>
                                                                </button>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                            {selectedCategory && (
                                                <div className="row mt-1">
                                                    <div className="prompt-templates-header mb-3" style={{ borderBottom: '1.5px solid #ddd' }}>
                                                        <button className="btn" onClick={handleBackClick}>
                                                            <i className="ki-outline ki-left-square fs-2x"></i>
                                                        </button>
                                                        <h2 className="pb-3" style={{ textAlign: "center", color: "#000", textTransform: "uppercase" }}>Prompt Suggestions</h2>
                                                        <h4 className="mb-3 text-center">{selectedCategory.name}</h4>
                                                    </div>
                                                    <div style={{ overflowY: 'auto', height: '460px' }}>
                                                        {selectedCategory.category_prompts.length > 0 ? (
                                                            selectedCategory.category_prompts.map((prompt) => (
                                                                <div key={prompt.id} className="col-12 mb-3">
                                                                    <div
                                                                        className={`prompt-card p-3 ${selectedPrompt === prompt.prompt ? 'selected' : ''}`}
                                                                        onClick={() => handlePromptClick(prompt.prompt)}
                                                                        style={{
                                                                            borderBottom: '1.5px solid #ddd',
                                                                            cursor: "pointer",
                                                                            color: selectedPrompt === prompt.prompt ? '#0a58ca' : '#2f2f2f',
                                                                        }}
                                                                    >
                                                                        <p className="prompts-text">
                                                                            <a className="prompts-link prompt-chat-links prompt-chat-26">{prompt.prompt}</a>
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <p>No prompts available for this category.</p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Second Column */}
                                <div className="your-prompt prompt-templates" style={{ width: '100%', height: '670px', marginTop: '30px', borderRadius: '10px', background: '#fff', padding: '20px', float: 'left', overflowY: 'auto' }}>
                                    <div>
                                        <div className="prompt-templates-header pt-4">
                                            <h2 className="pb-2" style={{ textAlign: "center", color: "#000", textTransform: "uppercase" }}>Create</h2>
                                            <hr />
                                        </div>
                                        <div id="#capon" className="chooseways toggle-tabs" style={{ marginTop: "30px" }}>
                                            <ul className="nav nav-tabs w-100">
                                                <li className="nav-item w-50">
                                                    <button className={`nav-link w-100 rounded-3 ${activeTab === "texts-tab" ? "active" : ""}`} onClick={() => setActiveTab("texts-tab")}>
                                                        Text
                                                    </button>
                                                </li>
                                                <li className="nav-item w-50">
                                                    <button className={`nav-link w-100 rounded-3 ${activeTab === "profile-tab" ? "active" : ""}`} onClick={() => setActiveTab("profile-tab")}>
                                                        Image
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="row pb-0 mt-10">
                                            <div className="col-md-12">
                                                <div>
                                                    {!captionsText.alertImgMsg ? (
                                                        <p className="alert alert-primary col-md-12 d-inline-block">No images available for this category.</p>
                                                    ) : (
                                                        <p className="text-alert-msg size" dangerouslySetInnerHTML={{ __html: captionsText.alertImgMsg }} />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row pb-3">
                                            {activeTab === "texts-tab" && (
                                                <div className="tab-content">
                                                    <p>Your prompt <i className="bi bi-info-circle-fill" style={{ fontSize: '18px' }}></i></p>
                                                    <div className="mb-3">
                                                        <div className="prompt-templates-content-c-2">
                                                            <textarea className="form-control" maxLength="1000" id="pasteArea" style={{ height: "150px", maxHeight: '200px', minHeight: '100px' }} rows="3" value={textareaValue} onChange={handleTextChange} placeholder="Enter your prompt here" />
                                                            <span id="char_left_txt_display-chat">{charCount} characters remaining</span>
                                                        </div>
                                                        <div>
                                                            <button
                                                                className="btn btn-ai w-100 fs-4 btn-shadow"
                                                                onClick={handleGenerateText}
                                                                disabled={(activeTab === "texts-tab" && !textareaValue.trim()) || (activeTab === "profile-tab" && !file) || loading}
                                                            >
                                                                {loading ? (
                                                                    <div className="d-flex align-items-center justify-content-center">
                                                                        <span className="ms-2">Please wait...</span>
                                                                        <div className="spinner-border" role="status">
                                                                            <span className="visually-hidden">Loading...</span>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div>
                                                                        Generate
                                                                        {captionsText.unlimitedImgGen === false && (
                                                                            <span> ({captionsText.imgCounts}/{captionsText.noOfImgAllow})</span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </button>
                                                            {error && (
                                                                <div className="alert alert-danger mt-3" role="alert">
                                                                    <span dangerouslySetInnerHTML={{ __html: error }} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {activeTab === "profile-tab" && (
                                                <div className="image-way">
                                                    <div className="prompt-templates-content-c-2" style={{ height: '285px', overflowY: 'auto' }}>
                                                        <p>Your Image <i className="bi bi-file-earmark-image-fill" style={{ fontSize: '18px' }}></i></p>
                                                        <div style={{ position: 'relative', height: generatedImage ? '150px' : '150px', minHeight: '150px', maxHeight: '300px', overflowY: 'auto', transition: 'height 0.3s ease' }}>
                                                            <span className="close-span close-span-chat" style={{ display: generatedImage ? 'block' : 'none' }}>
                                                                <a href="#asfd" style={{ textDecoration: 'none', backgroundColor: 'red', borderRadius: '50%', padding: '3px 7px', position: 'absolute', right: '9px', top: '7px' }} onClick={() => imgClose("mid")}>
                                                                    X
                                                                </a>
                                                            </span>
                                                            <form id="file-upload-form-chat" className="uploader chat">
                                                                <input id="file-upload-chat" type="file" name="file_chat" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
                                                                <label style={{ padding: generatedImage ? '1rem' : '3rem 1.5rem' }} htmlFor="file-upload-chat" id="file-drag-chat" className="mb-3">
                                                                    {generatedImage ? (
                                                                        <img src={generatedImage} alt="Preview" className="w-100 shadow-1-strong rounded" style={{ height: '100%', width: '100%' }} />
                                                                    ) : (
                                                                        <div id="start-chat" className="start-div">
                                                                            <i className="bi bi-upload" style={{ fontSize: '30px', cursor: 'pointer' }} />
                                                                            <div>Upload a file</div>
                                                                        </div>
                                                                    )}
                                                                </label>
                                                            </form>
                                                        </div>
                                                        <p>Your prompt <i className="bi bi-info-circle-fill" style={{ fontSize: '18px' }}></i></p>
                                                        <div className="prompt-templates-content-c-2">
                                                            <textarea className="form-control" maxLength="1000" style={{ height: "150px", maxHeight: '200px', minHeight: '100px' }} id="pasteArea" rows="3" value={textareaValue} onChange={handleTextChange} placeholder="Enter your prompt here" />
                                                            <span id="char_left_txt_display-chat">{charCount} characters remaining</span>
                                                        </div>
                                                    </div>
                                                    <button
                                                        className="btn btn-ai w-100 fs-4 btn-shadow mt-2"
                                                        onClick={handleGenerateText}
                                                        disabled={activeTab === "profile-tab" && (!generatedImage || !textareaValue.trim())}
                                                    >
                                                        {loading ? (
                                                            <div className="d-flex align-items-center justify-content-center">
                                                                <div className="spinner-border" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                                <span className="ms-2">Please wait...</span>
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                Generate
                                                                {captionsText.unlimitedImgGen === false && (
                                                                    <span> ({captionsText.imgCounts}/{captionsText.noOfImgAllow})</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </button>
                                                    {error && (
                                                        <div className="alert alert-danger mt-3" role="alert">
                                                            <span dangerouslySetInnerHTML={{ __html: error }} />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Third Column */}
                                <div id="#caponresult" className="prompt-result prompt-templates" style={{ width: '100%', marginTop: '30px', height: '670px', borderRadius: '10px', background: '#fff', padding: '20px', float: 'left' }}>
                                    <div style={{ backgroundColor: '#fff' }}>
                                        <div className="prompt-templates-header pt-4">
                                            <h2 className="pb-2" style={{ textAlign: "center", color: "#000", textTransform: "uppercase" }}>Result</h2>
                                            <hr />
                                        </div>
                                        <div className="row prompt-templates-content-c generated-result gen_chat_result" style={{ overflowY: 'auto', height: '540px' }}>
                                            <div className="col-md-12 col-12">
                                                {captionsText1 ? (
                                                    <div className="col-md-12 col-12 bg-light py-0" id="response_area">
                                                        <div className="row prompt-templates-content-c generated-result gen_mid_result">
                                                            <div className="col-md-12">
                                                                <div className="mid-container">
                                                                    <div className="mid-f-image mag-mid-f-image mb-3">
                                                                        <div className="image-grid">
                                                                            <img
                                                                                style={{ width: '100%', borderRadius: '6px', cursor: 'zoom-in' }}
                                                                                src={selectedImage || captionsText1[0]}
                                                                                alt="Selected Image"
                                                                                onClick={() => imgClick(selectedImage || captionsText1[0])}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="row" style={{ padding: '12px' }}>
                                                                        {captionsText1.map((item, index) => (
                                                                            <div className="col-md-3 mb-3 col-3 mid-imgs px-1" key={index} onClick={() => handleImageClick(item)}>
                                                                                <img
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        cursor: 'pointer',
                                                                                        borderRadius: '6px',
                                                                                        border: item === selectedImage ? '3px solid #fe363b' : 'none',
                                                                                        boxShadow: item === selectedImage ? '0 15px 45px rgb(254 54 59 / 35%)' : 'none',
                                                                                        filter: item === selectedImage ? 'brightness(70%)' : 'none',
                                                                                    }}
                                                                                    src={item}
                                                                                    alt={`Image ${index + 1}`}
                                                                                />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <button
                                                                            onClick={() => downloadImage(selectedImage || captionsText1[0])}
                                                                            className="btn btn-theme-rev btn-shadow-rev img-download-btn"
                                                                            disabled={isDownloading}
                                                                        >
                                                                            <span>{downloadStatus} <i className="bi bi-download"></i></span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <div className="col-md-12 col-12 bg-light py-5" id="response_area">
                                                            {error ? (
                                                                <div className="alert alert-danger mt-3" role="alert" style={{ textAlign: 'center', margin: '10px 10px' }}>
                                                                    <h3 style={{ textAlign: 'center' }}>Oops...!</h3>
                                                                    <span dangerouslySetInnerHTML={{ __html: error }} />
                                                                </div>
                                                            ) : (
                                                                <ol className="ps-7 pe-5">
                                                                    <li>Select a prompt from the <b>“Prompt Suggestions”</b> panel or write one from scratch.</li>
                                                                    <li className="mt-3 mb-3">Write or edit your prompt with what you want the AI to generate and hit the <b>Generate</b> button.</li>
                                                                    <li>The result is shown here.</li>
                                                                </ol>
                                                            )}
                                                        </div>
                                                        <div className="col-md-12 col-12">
                                                            <img className="w-100" src={hello} alt="" />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                    <div>
                        <Footer />
                    </div>
                    {isModalOpen && (
                        <div id="modalBackdrop" onClick={handleBackdropClick} className="modal fade show" style={{ display: 'block' }} aria-labelledby="exampleModalLabel">
                            <div className="modal-dialog modal-dialog-centered p-1">
                                <div className="modal-content" style={{ background: 'none', zIndex: 10002, border: 'none', boxShadow: 'none', padding: '0px' }}>
                                    <div className="modal-header btn-close23" style={{ background: 'transparent', border: 'none', boxShadow: 'none', border: 'none' }}>
                                        <button style={{ cursor: 'pointer', zIndex: 10001 }} type="button" className="btn-close fs-3x text-primary" onClick={handleModalClose} aria-label="Close"></button>
                                    </div>
                                    <div className="modal-body p-0">
                                        <button className="ms-1" style={{ position: 'absolute', top: '50%', left: '0px', transform: 'translateY(-50%)', zIndex: 112290, background: 'rgb(156 147 147 / 0%)', border: 'none', width: '50px', height: '100%', padding: '7px', color: '#ffffff73', cursor: 'pointer' }} onClick={handlePrevImage} aria-label="Previous Image">
                                            <i className="fa-solid fa-circle-chevron-left fs-3x"></i>
                                        </button>
                                        <img className="rounded-3" style={{ width: '100%', borderRadius: '6px' }} src={selectedImage || captionsText1[0]} alt="Selected Image" />
                                        <button className="me-1" style={{ position: 'absolute', top: '50%', right: '0px', width: '50px', height: '100%', transform: 'translateY(-50%)', zIndex: 10, color: '#ffffff73', background: 'rgb(156 147 147 / 0%)', border: 'none', padding: '0px', cursor: 'pointer' }} onClick={handleNextImage} aria-label="Next Image">
                                            <i className="fa-solid fa-circle-chevron-right fs-3x"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {isModalImgOpen && (
                        <div id="modalBackdrop" className="modal fade show" style={{ display: 'block' }} aria-labelledby="exampleModalLabel">
                            <div className="modal-dialog modal-dialog-centered">
                                <div className="modal-content">
                                    <div className="modal-header">Generate</div>
                                    <div className="modal-body">
                                        {genLoader()}
                                        <p className="fw-bold txt-primary text-center mt-3 mb-0 fs-4">Please wait</p>
                                        <p className="fw-bold txt-primary text-center fs-5 mb-0">This will take a minute.</p>
                                        <p id={`waiting-message-${type}`} className="txt-primary text-center fs-6" style={{ display: type === 'waiting' ? 'block' : 'none' }}>
                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span> Waiting....
                                        </p>
                                        <p id={`processing-message-${type}`} className="txt-primary text-center fs-6" style={{ display: type === 'processing' ? 'block' : 'none' }}>
                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span> Processing....
                                        </p>
                                        <p id={`installing-message-${type}`} className="txt-primary text-center fs-6" style={{ display: type === 'installing' ? 'block' : 'none' }}>
                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span> Downloading....
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Images;